import isEmpty from 'lodash/isEmpty';
import { ForwardedRef, forwardRef, MouseEvent, PropsWithoutRef } from 'react';
import styled from 'styled-components';

import { Locale } from 'modules/domains/template';
import { CounterBadge } from 'ui/badge';
import { ChevronDown16 } from 'ui/icons';
import { SelectValueDisplayProps, SelectValueType } from 'ui/selects';
import { spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';

import { LocaleLabelText } from './LocaleLabelText';

const DropdownButton = styled.button`
  align-items: center;
  background-color: transparent;
  border: none;
  display: flex;
  font-family: inherit;
  justify-content: space-between;
  padding: ${pxToRem(11)};
  text-align: left;
  width: 'fit-content';
`;

export const StyledCounterBadge = styled(CounterBadge)`
  margin: 0 ${spacing.half};
  background-color: ${({ theme }) => theme.colors.primaryColors.red100};
`;

export const LocaleSwitcherTrigger = ({
  templateDefaultLocale,
  errorCount = 0,
}: {
  templateDefaultLocale: Locale;
  errorCount: number;
}) =>
  forwardRef(function InnerSelectValue<TValue extends SelectValueType = string>(
    {
      isDisabled = false,
      isOpen = false,
      isNonInteractive = false,
      selectedOptions = [],
      toggleDropdown,
      ...props
    }: PropsWithoutRef<Omit<SelectValueDisplayProps<TValue>, 'content'>>,
    ref: ForwardedRef<HTMLButtonElement>,
  ) {
    const selectedOption = selectedOptions[0] ?? {};

    return (
      <DropdownButton
        ref={ref}
        data-test="basic-select-locale-switcher-trigger"
        aria-disabled={isDisabled}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        data-values={selectedOptions.map(({ value }) => value)}
        onClick={
          isNonInteractive
            ? undefined
            : (event: MouseEvent) => {
                if (typeof ref !== 'function') {
                  ref?.current?.focus(); // force focus for FF/Safari
                }
                event.stopPropagation();
                toggleDropdown();
              }
        }
        type="button"
        {...props}
      >
        {!isEmpty(selectedOption) && (
          <LocaleLabelText
            locale={selectedOption.data as Locale}
            isDefault={selectedOption.value === templateDefaultLocale.tag}
          />
        )}
        {errorCount > 0 && (
          <StyledCounterBadge data-test="locale-trigger-error-count">
            {errorCount}
          </StyledCounterBadge>
        )}
        <ChevronDown16 />
      </DropdownButton>
    );
  });
